import { IonIcon, IonInput, IonItem } from '@ionic/react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { lockClosedOutline, mailOutline } from 'ionicons/icons';

import { useAuthActions } from '../../../hooks/Auth/useAuth';
import { useAlert } from '../../../hooks/UI/useAlert';
import { useEnter } from '../../../hooks/Utils/useEnter';
import { Register as RegisterType, registerDefaults } from '../../../types/Auth/types';
import { validations } from '../../../utils/validations';
import Alert from '../../UI/Alert';
import Button from '../../UI/Button';
import FormItem from '../../UI/FormItem';
import Spinner from '../../UI/Spinner';
import './Register.scss';
import { RegisterProps } from './types';

const Register: React.FC<RegisterProps> = ({ setAuthMode }) => {
  const { register } = useAuthActions();
  const { ref, handleEnter } = useEnter();
  const { handleSubmit, control, errors } = useForm<RegisterType>({
    defaultValues: registerDefaults,
  });
  const { alertState, showAlert, hideAlert } = useAlert();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSubmit = async (values: RegisterType) => {
    setIsLoading(true);
    try {
      const body = {
        email: values.email.trim(),
        password: values.password,
        confirm: values.confirm,
      };

      const { success, error } = await register(body);

      if (success) {
        showAlert({
          header: '¡Éxito!',
          message: 'Tu cuenta se registró correctamente',
          buttons: [{ text: 'Iniciar sesión', handler: () => setAuthMode('login') }],
        });
      } else {
        showAlert({
          header: 'Error',
          message: error!,
          buttons: [{ text: 'Entendido'}],
        });
      }
    } catch (error) {
      console.error('Error en registro:', error);
      showAlert({
        header: 'Error',
        message: 'Ocurrió un error inesperado, intenta nuevamente',
        buttons: [{ text: 'Entendido' }],
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyUp={handleEnter}>
      <FormItem
        errors={errors}
        render={({ onChange }) => (
          <IonItem>
            <IonInput type="email" placeholder="Email" onIonChange={onChange}>
              <IonIcon icon={mailOutline} />
            </IonInput>
          </IonItem>
        )}
        control={control}
        name="email"
        rules={{
          required: 'Por favor, ingresá tu email',
          pattern: validations.email,
        }}
      />
      <FormItem
        errors={errors}
        render={({ onChange }) => (
          <IonItem>
            <IonInput type="password" placeholder="Contraseña" onIonChange={onChange}>
              <IonIcon icon={lockClosedOutline} />
            </IonInput>
          </IonItem>
        )}
        control={control}
        name="password"
        rules={{
          required: 'Por favor, ingresá tu contraseña',
          pattern: validations.password,
        }}
      />

      <FormItem
        errors={errors}
        render={({ onChange }) => (
          <IonItem>
            <IonInput
              type="password"
              placeholder="Confirmar contraseña"
              onIonChange={onChange}
            >
              <IonIcon icon={lockClosedOutline} />
            </IonInput>
          </IonItem>
        )}
        control={control}
        name="confirm"
        rules={{
          required: 'Por favor, confirmá tu contraseña',
          validate: (value) => {
            if (!value || control.getValues('password') === value) {
              return true;
            }
            return 'Las contraseñas no coinciden';
          },
        }}
      />

      <div className="ion-margin-top mt-aux">
        <Button ref={ref} type="submit" disabled={isLoading}>
          {!isLoading ? 'Registrarme' : <Spinner color="light" />}
        </Button>
      </div>

      <Alert
        isOpen={alertState.isOpen}
        header={alertState.header}
        message={alertState.message}
        buttons={alertState.buttons}
        onDidDismiss={hideAlert}
      />
    </form>
  );
};

export default Register;
